import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  FolderOpen,
  FileText,
  Download,
  ExternalLink,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Image,
  Sheet,
  File,
  Upload,
} from 'lucide-react';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function FileIcon({ mimeType }) {
  if (!mimeType) return <File className="w-4 h-4 text-slate-400" />;
  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-violet-500" />;
  if (mimeType.includes('pdf')) return <FileText className="w-4 h-4 text-red-500" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <Sheet className="w-4 h-4 text-green-600" />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="w-4 h-4 text-blue-600" />;
  return <File className="w-4 h-4 text-slate-400" />;
}

const FILE_TYPES = [
  { value: 'all', label: 'Alle types' },
  { value: 'pdf', label: 'PDF' },
  { value: 'image', label: 'Afbeeldingen' },
  { value: 'document', label: 'Documenten (Word)' },
  { value: 'spreadsheet', label: 'Spreadsheets (Excel)' },
];

const SOURCE_TYPES = [
  { value: 'all', label: 'Alle bronnen' },
  { value: 'manual', label: 'Handmatig' },
  { value: 'email', label: 'E-mail' },
  { value: 'document_upload', label: 'Document upload' },
];

/**
 * Reusable global files browser (extracted from the old FilesPage).
 *
 * - `paramPrefix`: namespaces the URL filter params so this browser can live as a
 *   tab inside Kennisbank without colliding with the Kennis tab's own params.
 *   Standalone usage = '' (legacy keys: location/type/source/search/page).
 *   Embedded usage  = 'f_' (f_location/f_type/f_source/f_search/f_page).
 * - `showTitle`: render the standalone "Bestanden" heading (hidden when embedded
 *   under the Bestanden tab, which already labels the view).
 *
 * The GET /api/files call and all file logic are unchanged from the original page.
 */
export function FilesBrowser({ paramPrefix = '', showTitle = true }) {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // Filters from URL params (namespaced by paramPrefix)
  const filterLocation = searchParams.get(`${paramPrefix}location`) || 'all';
  const filterType = searchParams.get(`${paramPrefix}type`) || 'all';
  const filterSource = searchParams.get(`${paramPrefix}source`) || 'all';
  const searchQuery = searchParams.get(`${paramPrefix}search`) || '';
  const page = parseInt(searchParams.get(`${paramPrefix}page`) || '1', 10);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    const pk = `${paramPrefix}${key}`;
    if (value === 'all' || value === '') {
      params.delete(pk);
    } else {
      params.set(pk, value);
    }
    if (key !== 'page') params.delete(`${paramPrefix}page`);
    setSearchParams(params);
  };

  // Clear only THIS browser's params — preserve ?tab= and the other tab's filters.
  const clearFilters = () => {
    const params = new URLSearchParams(searchParams);
    ['location', 'type', 'source', 'search', 'page'].forEach((key) => params.delete(`${paramPrefix}${key}`));
    setSearchParams(params);
  };
  const hasActiveFilters = filterLocation !== 'all' || filterType !== 'all' || filterSource !== 'all' || searchQuery;

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const params = new URLSearchParams();
      if (filterLocation !== 'all') params.append('location_id', filterLocation);
      if (filterType !== 'all') params.append('file_type', filterType);
      if (filterSource !== 'all') params.append('source', filterSource);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', page.toString());
      params.append('limit', '50');

      const [filesRes, locRes] = await Promise.all([
        api.get(`/files?${params}`),
        locations.length ? Promise.resolve({ data: locations }) : api.get('/locations'),
      ]);

      setFiles(filesRes.data.files || []);
      setTotal(filesRes.data.total || 0);
      setPages(filesRes.data.pages || 1);
      if (!locations.length) setLocations(locRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterLocation, filterType, filterSource, searchQuery, page, locations]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const locationMap = {};
  locations.forEach(l => { locationMap[l.location_id] = l.name; });

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="files-page">
      {/* Header with Upload button */}
      <div className="flex items-center justify-between">
        {showTitle ? (
          <div>
            <h2 className="font-heading text-sm uppercase tracking-wide text-slate-600 dark:text-[#8B949E]">Bestanden</h2>
          </div>
        ) : <div />}
        <Button
          onClick={() => navigate('/kennisbank/upload')}
          className="gap-2 bg-[#2E75B6] text-white hover:bg-[#27649C] h-10 sm:h-9"
          data-testid="files-upload-button"
        >
          <Upload className="w-4 h-4" />
          <span className="hidden sm:inline">Upload document</span>
        </Button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-3 sm:p-4" data-testid="files-filter-bar">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Locatie</label>
              <Select value={filterLocation} onValueChange={(v) => updateFilter('location', v)}>
                <SelectTrigger className="h-10 sm:h-9 text-sm" data-testid="files-filter-location">
                  <SelectValue placeholder="Alle locaties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle locaties</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Bestandstype</label>
              <Select value={filterType} onValueChange={(v) => updateFilter('type', v)}>
                <SelectTrigger className="h-10 sm:h-9 text-sm" data-testid="files-filter-type">
                  <SelectValue placeholder="Alle types" />
                </SelectTrigger>
                <SelectContent>
                  {FILE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Bron</label>
              <Select value={filterSource} onValueChange={(v) => updateFilter('source', v)}>
                <SelectTrigger className="h-10 sm:h-9 text-sm" data-testid="files-filter-source">
                  <SelectValue placeholder="Alle bronnen" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-56">
              <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Zoeken</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Bestandsnaam..."
                  className="h-9 text-sm pl-8"
                  data-testid="files-search-input"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="self-end h-9 px-2 text-xs text-slate-500 hover:text-slate-700 dark:text-[#8B949E] dark:hover:text-[#C9D1D9] flex items-center gap-1 transition-colors"
                data-testid="files-clear-filters"
              >
                <X className="w-3 h-3" /> Wis filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D]" data-testid="files-list">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-[#21262D] flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-900 dark:text-[#E6EDF3] font-heading">Bestanden</span>
          <span className="text-xs text-slate-400 tabular-nums">{total} bestanden</span>
        </div>

        {loading ? (
          <div className="p-4 space-y-3">
            {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : files.length === 0 ? (
          <div className="p-12 text-center">
            <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 dark:text-[#8B949E]" data-testid="files-empty-state">
              Geen bestanden gevonden voor de gekozen filters.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-[#21262D]">
            {files.map(file => (
              <FileRowGlobal
                key={file.file_id}
                file={file}
                locationMap={locationMap}
                onNavigate={() => navigate(`/kennisbank/${file.entry_id}`)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 dark:border-[#21262D] flex items-center justify-between">
            <span className="text-xs text-slate-400">Pagina {page} van {pages}</span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => updateFilter('page', (page - 1).toString())}
                className="h-7 px-2"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onClick={() => updateFilter('page', (page + 1).toString())}
                className="h-7 px-2"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FileRowGlobal({ file, locationMap, onNavigate }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const { accessTokenRef } = useAuth();
  const [thumbUrl, setThumbUrl] = React.useState(null);
  const [thumbError, setThumbError] = React.useState(false);

  // Load image thumbnail with auth
  React.useEffect(() => {
    if (!file.mime_type?.startsWith('image/')) return;
    let revoke = null;
    const url = `${API}/api/attachments/${file.file_id}`;
    const token = accessTokenRef?.current;
    if (!token) return;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(res.status); return res.blob(); })
      .then(blob => { revoke = URL.createObjectURL(blob); setThumbUrl(revoke); })
      .catch(() => { setThumbError(true); });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.file_id]);

  const handleDownload = (e) => {
    e.stopPropagation();
    const token = accessTokenRef?.current;
    if (!token) return;
    const url = `${API}/api/attachments/${file.file_id}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) throw new Error(res.status); return res.blob(); })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.original_name;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {});
  };

  return (
    <div
      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors cursor-pointer group"
      onClick={onNavigate}
      data-testid={`file-row-${file.file_id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {file.mime_type?.startsWith('image/') && thumbUrl && !thumbError ? (
          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 dark:bg-[#21262D]">
            <img
              src={thumbUrl}
              alt={file.original_name}
              className="w-full h-full object-cover"
              onError={() => setThumbError(true)}
            />
          </div>
        ) : (
          <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[#21262D] flex items-center justify-center flex-shrink-0">
            <FileIcon mimeType={file.mime_type} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3] truncate group-hover:text-[#2E75B6]">{file.original_name}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[10px] text-slate-400">{formatFileSize(file.size)}</span>
            <span className="text-[10px] text-slate-400">{formatDate(file.uploaded_at)}</span>
            {file.location_id && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-[#21262D] text-slate-500 dark:text-[#8B949E]">
                {locationMap[file.location_id] || file.location_id}
              </span>
            )}
            {file.source && file.source !== 'manual' && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400">
                {file.source === 'email' ? 'E-mail' : file.source === 'document_upload' ? 'Document' : file.source}
              </span>
            )}
            {file.entry_title && (
              <span className="text-[10px] text-slate-400 truncate max-w-[200px]">{file.entry_title}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <button
          onClick={handleDownload}
          className="p-2 sm:p-1.5 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
          title="Download"
          data-testid={`download-${file.file_id}`}
        >
          <Download className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(); }}
          className="p-2 sm:p-1.5 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
          title="Naar item"
        >
          <ExternalLink className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        </button>
      </div>
    </div>
  );
}
